import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import GameMap from './logic/GameMap';
import { mapConfigs } from './MapConfig/mapConfigs';
import BattleTowerModal from '../../components/Modals/BattleTowerModal';
import api from '../../api';

const BattleTowerEntrance = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedTower, setSelectedTower] = useState('60second');
  const [playerPosition, setPlayerPosition] = useState({ x: 0, y: 0 });
  const navigate = useNavigate();

  // Function to purchase tower access
  const purchaseTowerAccess = useCallback(async (towerType) => {
    try {
      console.log(`Purchasing access to ${towerType} tower`);
      const response = await api.post('/v1/battle-tower/access', {
        tower_type: towerType,
        cost: 0 // Free access
      }, { withCredentials: true });
      
      console.log('Purchase response:', response.data);
      
      if (response.data.success) {
        // Navigate to the tower with a refresh flag to ensure progress is reloaded
        const navigationState = { forceRefresh: true };
        switch(towerType) {
          case 'original':
            navigate('/battle-tower', { state: navigationState });
            break;
          case '60second':
            navigate('/battle-tower/60second', { state: navigationState });
            break;
          case 'endless':
            navigate('/battle-tower/endless', { state: navigationState });
            break;
          default:
            navigate('/battle-tower', { state: navigationState });
        }
      } else {
        console.error('Failed to purchase access:', response.data.message);
      }
    } catch (error) {
      console.error('Error purchasing tower access:', error);
    }
  }, [navigate]);

  // Function to check Battle Tower access status
  const checkTowerAccess = useCallback(async () => {
    try {
      const response = await api.get('/v1/battle-tower/progress?tower_type=60second', { withCredentials: true });
      
      // Check if user has active access to a tower
      const data = response.data.data || response.data;
      console.log('Tower progress data:', data);
      
      // Check for active access based on the API response format
      const has60SecondAccess = data.has_active_access === true || 
                              (data.access_expiration && new Date(data.access_expiration) > new Date());
      
      const towerType = data.tower_type || '60second';
      
      if (has60SecondAccess) {
        console.log(`User has active access to ${towerType} tower`);
        // Navigate based on tower type
        switch(towerType) {
          case 'original':
            navigate('/battle-tower');
            break;
          case '60second':
            navigate('/battle-tower/60second');
            break;
          case 'endless':
            navigate('/battle-tower/endless');
            break;
          default:
            navigate('/battle-tower');
        }
      }
    } catch (error) {
      console.error('Error checking Battle Tower access:', error);
      // If API doesn't exist yet, just continue without redirecting
    }
  }, [navigate]);

  // Check access when component mounts
  useEffect(() => {
    checkTowerAccess();
  }, [checkTowerAccess]);

  // Handle NPC interaction to show Battle Tower modal
  const handleNpcInteraction = (npcId, playerPos) => {
    if (npcId === 'SafraiZone') {
      setPlayerPosition(playerPos);
      // Show the Battle Tower modal
      setShowModal(true);
    }
  };
  
  // Handle tower selection and entry
  const handleTowerEntry = () => {
    // Close the modal
    setShowModal(false);
    
    // Purchase access to the selected tower
    purchaseTowerAccess(selectedTower);
  };

  // Function to handle access granted from modal
  const handleAccessGranted = (accessData) => {
    setShowModal(false);
    
    // Navigate to appropriate tower based on type
    setTimeout(() => {
      switch(accessData.tower_type) {
        case '60second':
          navigate('/battle-tower/60second');
          break;
        case 'endless':
          navigate('/battle-tower/endless');
          break;
        default:
          navigate('/battle-tower');
      }
    }, 500); // Small delay for better UX
  };

  // Function to spawn floating text (passed to modal)
  const spawnText = (text, x, y, color) => {
    // Create a floating text element
    const textElement = document.createElement('div');
    textElement.innerText = text;
    textElement.style.position = 'absolute';
    textElement.style.left = `${x}px`;
    textElement.style.top = `${y}px`;
    textElement.style.color = color || '#ffffff';
    textElement.style.fontSize = '16px';
    textElement.style.fontWeight = 'bold';
    textElement.style.textShadow = '1px 1px 2px rgba(0, 0, 0, 0.7)';
    textElement.style.zIndex = 1000;
    textElement.style.pointerEvents = 'none';
    textElement.style.animation = 'floatUp 2s forwards';
    
    // Add the element to the DOM
    document.body.appendChild(textElement);
    
    // Remove the element after animation completes
    setTimeout(() => {
      document.body.removeChild(textElement);
    }, 2000);
  };

  // Add CSS for floating text animation if it doesn't exist
  useEffect(() => {
    if (!document.getElementById('float-text-style')) {
      const style = document.createElement('style');
      style.id = 'float-text-style';
      style.innerHTML = `
        @keyframes floatUp {
          0% { transform: translateY(0); opacity: 1; }
          100% { transform: translateY(-30px); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }
    
    return () => {
      const styleElement = document.getElementById('float-text-style');
      if (styleElement) {
        document.head.removeChild(styleElement);
      }
    };
  }, []);

  return (
    <>
      <GameMap 
        config={mapConfigs.battletowerentrance} 
        onNpcInteraction={handleNpcInteraction}
        onPlayerPositionChange={setPlayerPosition}
      />
      
      <BattleTowerModal
        isOpen={showModal}
        toggle={() => setShowModal(false)}
        playerPos={playerPosition}
        spawnText={spawnText}
        onAccessGranted={handleAccessGranted}
        selectedTower={selectedTower}
        setSelectedTower={setSelectedTower}
      />
    </>
  );
};

export default BattleTowerEntrance;
